import { useState } from 'react'
import { useComponents, useDeleteComponent } from '../hooks/useComponents'
import { useMe } from '../hooks/useMe'
import { useArchived } from '../contexts/ArchiveContext'
import { ComponentEditor } from './ComponentEditor'
import { Icon } from './Icon'
import { COMPONENT_CATEGORIES } from '../types'
import type { Component, ComponentCategory, ComponentStatus } from '../types'

// ComponentsTab - per-event production components, grouped into collapsible
// sections by category. Deviates from the Activities flat-card-grid because
// components naturally group by category (locomotive, coaches, supplier,
// ops resource, programme, hospitality, other) and the count per category
// gets large quickly for big events. Single-click on any row opens the editor.
//
// All seven categories render always (even empty ones), each with a per-
// category "+ Add" CTA. Empty categories start collapsed; non-empty start
// expanded. The user can toggle any category section freely.
//
// Financial gating display:
//   - Cost column shows in the row only if me.hasFinancial is true. The
//     server returns null for non-financial users regardless, so this is
//     belt-and-braces. Other columns (status, quantity, attachments) render
//     for everyone.

interface Props {
  eventId: number
}

export function ComponentsTab({ eventId }: Props) {
  const archived = useArchived()
  const { data: components, isLoading, error } = useComponents(eventId)
  const { data: me } = useMe()
  const hasFinancial = me?.hasFinancial ?? false
  const deleteMut = useDeleteComponent(eventId)

  const [editing, setEditing] = useState<Component | null>(null)
  // null = no create modal open; otherwise the category to pre-select.
  const [creatingIn, setCreatingIn] = useState<ComponentCategory | null>(null)
  // Track which categories are collapsed. Default = empty categories collapsed.
  // We compute the default lazily once components load (see below).
  const [collapsed, setCollapsed] = useState<Set<ComponentCategory> | null>(null)

  if (isLoading) {
    return <div className="text-ink-soft text-sm font-mono">Loading components…</div>
  }

  if (error) {
    return (
      <div className="bg-oxblood-soft border border-oxblood text-oxblood rounded p-4 text-sm">
        <div className="font-semibold mb-1">Could not load components</div>
        <pre className="text-xs whitespace-pre-wrap">{(error as Error).message}</pre>
      </div>
    )
  }

  const list = components ?? []

  // Group rows by category. We iterate the canonical order so the result
  // map's iteration order matches what we display.
  const byCategory: Record<ComponentCategory, Component[]> = {
    'Locomotive':    [],
    'Coaches':       [],
    'Supplier':      [],
    'Ops Resource':  [],
    'Programme':     [],
    'Hospitality':   [],
    'Logistics':     [],
    'Safety':        [],
    'Comms':         [],
    'Accessibility': [],
    'Onboarding':    [],
    'F&B':           [],
    'Retail':        [],
    'Other':         []
  }
  for (const c of list) byCategory[c.category].push(c)

  // First render: collapse all empty categories so the page isn't a wall of
  // headers when only a couple of categories have content. Once initialised,
  // user toggles take over.
  const collapsedSet =
    collapsed ??
    new Set<ComponentCategory>(
      COMPONENT_CATEGORIES.filter((c) => byCategory[c].length === 0)
    )

  function toggleCategory(c: ComponentCategory) {
    const next = new Set(collapsedSet)
    if (next.has(c)) next.delete(c)
    else next.add(c)
    setCollapsed(next)
  }

  // Empty-state - top-level CTA when nothing has been added at all.
  const totalCount = list.length

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="font-display text-xl">Approvals</h2>
          <p className="text-sm text-ink-soft mt-0.5">
            Production components for this event &mdash; rolling stock, suppliers,
            ops resources, programme, hospitality. Click a row to edit.
          </p>
        </div>
        {totalCount > 0 && (
          <span className="text-xs text-ink-soft font-mono whitespace-nowrap pb-1">
            {totalCount} item{totalCount === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {totalCount === 0 ? (
        <div className="bg-white border border-line rounded p-10 text-center">
          <div
            className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center"
            style={{ background: '#f5edd5', color: '#9b7f2a' }}
          >
            <Icon name="box" size={22} />
          </div>
          <h3 className="font-display text-lg mb-1">No components yet</h3>
          <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
            Components capture everything you need to put on the event &mdash;
            locomotive, coaches, suppliers, performers, marquees. Pick a
            category to add the first one.
          </p>
          {!archived && (
            <button
              type="button"
              onClick={() => setCreatingIn('Locomotive')}
              className="px-4 py-2 rounded text-sm font-semibold bg-green text-parchment hover:bg-green/90 transition-colors"
            >
              + Add the first component
            </button>
          )}
        </div>
      ) : null}

      <div className="space-y-3">
        {COMPONENT_CATEGORIES.map((cat) => {
          const items = byCategory[cat]
          const isCollapsed = collapsedSet.has(cat)
          return (
            <CategorySection
              key={cat}
              category={cat}
              items={items}
              collapsed={isCollapsed}
              hasFinancial={hasFinancial}
              archived={archived}
              onToggle={() => toggleCategory(cat)}
              onAdd={() => setCreatingIn(cat)}
              onRowClick={(c) => setEditing(c)}
            />
          )
        })}
      </div>

      {!archived && creatingIn && (
        <ComponentEditor
          eventId={eventId}
          component={null}
          defaultCategory={creatingIn}
          onClose={() => setCreatingIn(null)}
        />
      )}

      {!archived && editing && (
        <ComponentEditor
          eventId={eventId}
          component={editing}
          deleting={deleteMut.isPending}
          onClose={() => setEditing(null)}
          onDelete={() => {
            const ok = window.confirm(
              `Delete "${editing.name}"? This can't be undone.`
            )
            if (!ok) return
            deleteMut.mutate(editing.id, {
              onSuccess: () => setEditing(null)
            })
          }}
        />
      )}
    </div>
  )
}

// ---- Category section ------------------------------------------------------

function CategorySection({
  category, items, collapsed, hasFinancial, archived, onToggle, onAdd, onRowClick
}: {
  category: ComponentCategory
  items: Component[]
  collapsed: boolean
  hasFinancial: boolean
  archived: boolean
  onToggle: () => void
  onAdd: () => void
  onRowClick: (c: Component) => void
}) {
  const empty = items.length === 0

  return (
    <div className="bg-white border border-line rounded">
      <div className="flex items-center gap-3 px-4 py-3">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={!collapsed}
          aria-controls={`category-${category}`}
          className="flex items-center gap-2 flex-1 text-left text-sm font-semibold hover:text-green transition-colors"
        >
          <span aria-hidden className="text-ink-soft text-xs font-mono w-2">
            {collapsed ? '▶' : '▼'}
          </span>
          <span>{category}</span>
          <span className="text-xs text-ink-soft font-mono ml-1">
            {items.length}
          </span>
        </button>
        {!archived && (
          <button
            type="button"
            onClick={onAdd}
            className="text-xs font-semibold text-green hover:underline whitespace-nowrap"
          >
            + Add
          </button>
        )}
      </div>

      {!collapsed && (
        <div id={`category-${category}`} className="border-t border-line">
          {empty ? (
            <div className="px-4 py-4 text-xs italic text-ink-soft">
              No {category.toLowerCase()} components yet.
            </div>
          ) : (
            <div className="divide-y divide-line">
              {items.map((c) => (
                <ComponentRow
                  key={c.id}
                  component={c}
                  hasFinancial={hasFinancial}
                  onClick={archived ? undefined : () => onRowClick(c)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ---- Component row ---------------------------------------------------------

// One row in a category section. Built as a <button> so it's keyboard-
// accessible. Layout is: status pill (left) | name + description (centre,
// flex-1) | meta column (right - qty / cost / attachments).
function ComponentRow({
  component, hasFinancial, onClick
}: {
  component: Component
  hasFinancial: boolean
  onClick: (() => void) | undefined
}) {
  const cancelled = component.status === 'Cancelled'
  const interactive = onClick != null
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!interactive}
      className={
        'group w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ' +
        (interactive ? 'hover:bg-parchment focus:outline-none focus:bg-parchment' : 'cursor-default')
      }
    >
      <span
        aria-hidden
        className="pt-1 text-line opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
        title="Reorder coming in a later phase"
      >
        <Icon name="grip" size={12} />
      </span>
      <div className="pt-0.5 flex-shrink-0">
        <StatusPill status={component.status} />
      </div>

      <div className="flex-1 min-w-0">
        <div
          className={`text-sm font-semibold leading-tight ${cancelled ? 'line-through text-ink-soft' : ''}`}
        >
          {component.name}
        </div>
        {component.description && (
          <p
            className="text-xs text-ink-soft leading-snug mt-1"
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}
          >
            {component.description}
          </p>
        )}
      </div>

      <div className="flex flex-col items-end gap-0.5 text-xs flex-shrink-0 font-mono pt-0.5">
        {component.quantity != null && (
          <div className="text-ink-soft">×{component.quantity}</div>
        )}
        {hasFinancial && component.costEstimate != null && (
          <div className="text-ink">
            £{component.costEstimate.toLocaleString('en-GB', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
          </div>
        )}
        {component.attachments.length > 0 && (
          <div className="text-green">
            {component.attachments.length} link{component.attachments.length === 1 ? '' : 's'}
          </div>
        )}
      </div>
    </button>
  )
}

// ---- Status pill -----------------------------------------------------------

function StatusPill({ status }: { status: ComponentStatus }) {
  const styles: Record<ComponentStatus, string> = {
    'Proposed':  'bg-parchment text-ink-soft border-line',
    'Confirmed': 'bg-green-soft text-green border-green',
    'Cancelled': 'bg-oxblood-soft text-oxblood border-oxblood'
  }
  return (
    <span className={`px-2 py-0.5 text-xs font-semibold border rounded ${styles[status]} whitespace-nowrap`}>
      {status}
    </span>
  )
}
